/**
 * Fullscreen "show all chapters" overlay for the History page.
 * Structurally modeled on details-modal.js (details/summary + portal-to-body
 * on open), but fades in instead of sliding. Its list is populated by cloning
 * each chapter's own hidden [data-history-overlay-row] template (rendered by
 * sections/history-chapter.liquid) rather than building elements from data
 * attributes — that template already has the real year/title/description
 * markup, so there's nothing to keep in sync here.
 */
class HistoryOverlay extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.modal = this.querySelector('.history-overlay__modal');
    this.backdrop = this.querySelector('.history-overlay__backdrop');
    this.list = this.querySelector('[data-history-overlay-list]');

    this.modal.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('.history-overlay__close').addEventListener('click', this.close.bind(this));
    this.list.addEventListener('click', this.onListClick.bind(this));

    this.summaryToggle.setAttribute('role', 'button');

    this.setupDragToScroll();
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    const isInside = this.contains(event.target) || this.modal.contains(event.target);
    if (!isInside) this.close(false);
  }

  populateList() {
    if (this.list.childElementCount > 0) return;

    document.querySelectorAll('[data-history-overlay-row]').forEach((template) => {
      const content = template.cloneNode(true);
      const targetId = content.dataset.targetId;
      if (!targetId) return;

      content.removeAttribute('hidden');
      content.removeAttribute('data-history-overlay-row');
      content.removeAttribute('data-target-id');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'history-overlay__row-button';
      button.dataset.targetId = targetId;
      button.append(content);

      const row = document.createElement('li');
      row.className = 'history-overlay__row';
      row.append(button);

      this.list.append(row);
    });

    this.setupActiveRowTracking();
  }

  // Whichever row is centered in the viewport gets .is-active — component-
  // history-overlay.css uses it to fade everything else's texts down to
  // hidden and its year down to a faint 0.08. Inactive rows get aria-disabled
  // (not the disabled attribute — real disabled buttons don't fire click at
  // all, which would break clicking them to recenter, see onListClick).
  setupActiveRowTracking() {
    const buttons = this.list.querySelectorAll('.history-overlay__row-button');
    if (!buttons.length) return;

    this.rowObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-active', entry.isIntersecting);
          entry.target.setAttribute('aria-disabled', !entry.isIntersecting);
        });
      },
      { rootMargin: '-50% 0px -50% 0px' }
    );

    buttons.forEach((button) => this.rowObserver.observe(button));
  }

  // Mouse-only click-and-drag scrolling for the list — touch already gets
  // native swipe scrolling for free from overflow-y: auto. Only starts from
  // the year column: starting it anywhere in the row (texts, learn-more,
  // the close button near the edge of the screen) made it too easy to turn
  // an intended click into a suppressed "drag".
  setupDragToScroll() {
    let isDragging = false;
    let startY = 0;
    let startScrollTop = 0;

    this.list.addEventListener('pointerdown', (event) => {
      // Reset unconditionally: this must happen for every pointerdown, not
      // just ones that go on to start a drag — otherwise, after any one
      // drag from the year column, this.dragged stays stuck true and
      // onListClick bails out on every later click anywhere in the row.
      this.dragged = false;

      if (event.pointerType !== 'mouse') return;
      if (!event.target.closest('.history-overlay__year')) return;

      isDragging = true;
      startY = event.clientY;
      startScrollTop = this.list.scrollTop;
      this.list.setPointerCapture(event.pointerId);
      this.list.classList.add('is-dragging');
    });

    this.list.addEventListener('pointermove', (event) => {
      if (!isDragging) return;

      const deltaY = event.clientY - startY;
      if (Math.abs(deltaY) > 5) this.dragged = true;
      this.list.scrollTop = startScrollTop - deltaY;
    });

    const endDrag = () => {
      isDragging = false;
      this.list.classList.remove('is-dragging');
    };

    this.list.addEventListener('pointerup', endDrag);
    this.list.addEventListener('pointercancel', endDrag);
  }

  // Jumps (no smooth scroll — this should look instant, hidden behind the
  // fade-in) so the overlay opens with the chapter you were already viewing
  // centered, instead of always starting at the top of the list.
  scrollToActiveChapter() {
    const activeChapterId = this.closest('history-timeline-nav')?.dataset.activeChapterId;
    if (!activeChapterId) return;

    this.list.querySelector(`[data-target-id="${activeChapterId}"]`)?.scrollIntoView({ block: 'center' });
  }

  onListClick(event) {
    if (this.dragged) return; // this click is the tail end of a drag gesture, not a real click

    const button = event.target.closest('[data-target-id]');
    if (!button) return;

    // Inactive (aria-disabled) row: bring it to the middle of the list
    // instead of navigating — only the already-active/centered row's click
    // closes the overlay and scrolls the page to that chapter.
    if (!button.classList.contains('is-active')) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const target = document.getElementById(button.dataset.targetId);
    this.close();
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.cancelClosing();
    this.populateList();
    event.target.closest('details').setAttribute('open', true);

    // Portal to <body> so position: fixed escapes any transformed/sticky ancestor.
    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.modal);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    // Commit the closed state before adding is-open, otherwise the browser
    // collapses both into one frame and skips the fade-in.
    void this.modal.offsetWidth;
    this.scrollToActiveChapter();
    this.modal.classList.add('is-open');
    this.backdrop.classList.add('is-open');

    trapFocus(this.modal, this.modal.querySelector('.history-overlay__close'));
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');

    this.cancelClosing();
    this.modal.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishClose();
      return;
    }

    this.finishCloseHandler = this.finishClose.bind(this);
    this.modal.addEventListener('transitionend', this.finishCloseHandler);
    this.closeTimeout = setTimeout(this.finishCloseHandler, 600);
  }

  cancelClosing() {
    clearTimeout(this.closeTimeout);
    if (this.finishCloseHandler) {
      this.modal.removeEventListener('transitionend', this.finishCloseHandler);
    }
  }

  finishClose(event) {
    // Only react to the modal's own fade transition, not other props or bubbling.
    if (event && (event.target !== this.modal || event.propertyName !== 'opacity')) return;
    this.cancelClosing();
    this.detailsContainer.appendChild(this.backdrop);
    this.detailsContainer.appendChild(this.modal);
  }
}

customElements.define('history-overlay', HistoryOverlay);
